import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, InputBase, Popover, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import InsertEmoticonOutlinedIcon from "@mui/icons-material/InsertEmoticonOutlined";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { makeStyles } from "tss-react/mui";
import { i18n } from "../../translate/i18n";

const FORMATS = [
  { key: "bold", mark: "*", Icon: FormatBoldIcon },
  { key: "italic", mark: "_", Icon: FormatItalicIcon },
  { key: "strike", mark: "~", Icon: StrikethroughSIcon },
];

const useStyles = makeStyles()((theme) => ({
  label: {
    fontSize: 12.5,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
  },
  required: {
    color: theme.palette.error.main,
    marginLeft: 2,
  },
  box: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    overflow: "hidden",
    background: theme.palette.background.default,
    transition: "border-color .15s",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
  },
  boxError: {
    borderColor: theme.palette.error.main,
  },
  input: {
    width: "100%",
    padding: theme.spacing(1.25, 1.5),
    fontSize: 14,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    padding: theme.spacing(0.25, 0.75),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  divider: {
    width: 1,
    height: 18,
    background: theme.palette.divider,
    margin: theme.spacing(0, 0.5),
  },
  helper: {
    fontSize: 12,
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  helperError: {
    color: theme.palette.error.main,
  },
}));

const MessageField = ({ label, value, onChange, error, helperText, placeholder, required = true }) => {
  const { classes, cx } = useStyles();
  const inputRef = useRef(null);
  const pendingSelection = useRef(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const text = value ?? "";

  useEffect(() => {
    const input = inputRef.current;
    if (!pendingSelection.current || !input) return;
    const [start, end] = pendingSelection.current;
    pendingSelection.current = null;
    input.focus();
    input.setSelectionRange(start, end);
  }, [text]);

  const replaceSelection = (build) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    const { insert, selectFrom, selectTo } = build(text.slice(start, end));
    pendingSelection.current = [start + selectFrom, start + selectTo];
    onChange(text.slice(0, start) + insert + text.slice(end));
  };

  const wrapSelection = (mark) =>
    replaceSelection((selected) => ({
      insert: `${mark}${selected}${mark}`,
      selectFrom: mark.length,
      selectTo: mark.length + selected.length,
    }));

  const insertEmoji = (emoji) => {
    replaceSelection(() => ({
      insert: emoji.native,
      selectFrom: emoji.native.length,
      selectTo: emoji.native.length,
    }));
    setEmojiAnchor(null);
  };

  return (
    <Box>
      {label && (
        <Typography className={classes.label}>
          {label}
          {required && <span className={classes.required}>*</span>}
        </Typography>
      )}
      <div className={cx(classes.box, error && classes.boxError)}>
        <InputBase
          inputRef={inputRef}
          className={classes.input}
          multiline
          minRows={2}
          maxRows={8}
          value={text}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        <div className={classes.toolbar}>
          {FORMATS.map(({ key, mark, Icon }) => (
            <Tooltip key={key} title={i18n.t(`settings.attention.survey.format.${key}`)}>
              <IconButton size="small" onClick={() => wrapSelection(mark)}>
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
          <span className={classes.divider} />
          <Tooltip title={i18n.t("settings.attention.survey.format.emoji")}>
            <IconButton size="small" onClick={(e) => setEmojiAnchor(e.currentTarget)}>
              <InsertEmoticonOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {helperText && (
        <Typography className={cx(classes.helper, error && classes.helperError)}>{helperText}</Typography>
      )}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Picker data={data} perLine={9} previewPosition="none" skinTonePosition="none" onEmojiSelect={insertEmoji} />
      </Popover>
    </Box>
  );
};

export default MessageField;
